/**
 * BuchhaltungBank — Transaktionen wie sevdesk:
 * Konten-Filter, Status-Reiter (Offen / Gebucht / Gelöscht / Alle), Suche,
 * Zuordnen-Dialog (Rechnung/Beleg mit begründeter Differenz oder ohne Beleg),
 * Drei-Punkte-Menü (Kredit, Privatentnahme, Geldbewegung, Gebühren, Löschen).
 * Buchungen ausschließlich über fibu_reconcile (eine Transaktion, GoBD).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router-dom';
import { Wallet, Search, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '@/components/ui/PageHeader';
import PageShell from '@/components/ui/PageShell';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useAuth } from '@/api/AuthContext';
import { listConnections, listOpenItems, fmtEUR } from '@/lib/buchhaltungApi';
import { financeSync, syncSummary } from '@/lib/financeApi';
import { listTransactions, countTransactions, unreconcile, deleteTransaction, restoreTransaction } from '@/lib/reconcileApi';
import { rankCandidates, txRest } from '@/lib/reconcileLogic';
import StatusTabs from '@/components/ausgaben/StatusTabs';
import { CARD, FIELD, BTN_PRIMARY, BTN_SECONDARY, LABEL } from '@/components/ausgaben/dna';
import BankConnectionsPanel from '@/components/bank/BankConnectionsPanel';
import KontenImport from '@/components/bank/KontenImport';
import TransactionTable from '@/components/bank/TransactionTable';
import ReconcileSheet from '@/components/bank/ReconcileSheet';
import AbgleichsquoteCard from '@/components/bank/AbgleichsquoteCard';
import AbgleichKontenCard from '@/components/bank/AbgleichKontenCard';

// Bank-Rückleitung kann auch hier landen (in der Enable-Banking-App
// eingetragene URL) → an die Verbinden-Seite weiterreichen.
export default function BuchhaltungBank() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if ((params.get('code') && params.get('state')) || params.get('error')) {
    return <Navigate to={`/buchhaltung/bank/verbinden${location.search}`} replace />;
  }
  return <BuchhaltungBankInner />;
}

const norm = (s) => String(s || '').toLowerCase();

function matchesSearch(tx, q) {
  if (!q) return true;
  const amount = Number(q.replace(/\./g, '').replace(',', '.'));
  return norm(tx.counterparty_name).includes(q) || norm(tx.purpose).includes(q)
    || (Number.isFinite(amount) && amount > 0 && Math.abs(Math.abs(Number(tx.amount)) - amount) < 0.005);
}

/** Grund abfragen (Löschen = Pflicht, Aufheben = optional) */
function ReasonDialog({ state, onClose }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(''); }, [state?.tx?.id, state?.kind]);
  if (!state) return null;
  const del = state.kind === 'delete';
  const run = async () => {
    setBusy(true);
    try { await state.run(reason.trim()); onClose(true); } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{del ? 'Umsatz löschen' : 'Zuordnung aufheben'}</DialogTitle>
          <DialogDescription>
            {del
              ? 'Der Umsatz wird ausgeblendet (nicht physisch gelöscht) und beim nächsten Abruf nicht erneut importiert. Unter „Gelöscht“ wiederherstellbar.'
              : 'Die Buchung wird per Gegenbuchung storniert; Rechnungen/Belege sind danach wieder offen.'}
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm text-slate-700">{state.tx.counterparty_name || '—'} · <span className="tabular-nums">{fmtEUR(state.tx.amount)}</span></p>
        <label className="block">
          <span className={LABEL}>Grund{del ? '' : ' (optional)'}</span>
          <Input autoFocus value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200}
            placeholder={del ? 'z. B. Doppelt importiert' : 'z. B. Falsche Rechnung'} className={cn(FIELD, 'mt-1')} />
        </label>
        <DialogFooter className="gap-2">
          <Button variant="outline" className={BTN_SECONDARY} onClick={() => onClose(false)}>Abbrechen</Button>
          <Button className={cn(BTN_PRIMARY, del && 'bg-red-700 hover:bg-red-800')} disabled={busy || (del && !reason.trim())} onClick={run}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}{del ? 'Löschen' : 'Aufheben'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BuchhaltungBankInner() {
  const { user } = useAuth();
  const actor = user?.email || null;
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('offen');
  const [connectionId, setConnectionId] = useState(null);
  const [search, setSearch] = useState('');
  const [sheet, setSheet] = useState(null);     // { tx, mode, category, preselect }
  const [reasonState, setReasonState] = useState(null);

  const { data: connections = [] } = useQuery({ queryKey: ['fibu-connections'], queryFn: listConnections });
  const { data: openItems = [] } = useQuery({ queryKey: ['fibu-open-items'], queryFn: () => listOpenItems() });
  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ['fibu-transactions', tab, connectionId],
    queryFn: () => listTransactions({ tab, connectionId }),
  });
  const { data: counts = {} } = useQuery({
    queryKey: ['fibu-transaction-counts', connectionId],
    queryFn: () => countTransactions({ connectionId }),
  });

  const refresh = () => {
    ['fibu-transactions', 'fibu-transaction-counts', 'fibu-open-items', 'fibu-reconcile-stats', 'fibu-connections', 'finance-status', 'fibu-tx-unmatched']
      .forEach((k) => queryClient.invalidateQueries({ queryKey: [k] }));
  };

  // Beim Öffnen Bank/PayPal abrufen (Server respektiert das Mindestintervall)
  const [syncInfo, setSyncInfo] = useState(null);
  const syncRan = useRef(false);
  useEffect(() => {
    if (syncRan.current) return;
    syncRan.current = true;
    financeSync({ force: false }).then((res) => {
      const s = syncSummary(res);
      if ((res.results || []).some((r) => r.status === 'ok')) setSyncInfo(`Abruf: ${s.text}`);
      if (s.imported > 0 || s.autoMatched > 0) {
        refresh();
        if (s.autoMatched > 0) toast.success(`${s.autoMatched} Zahlung(en) automatisch zugeordnet`);
      }
    }).catch((err) => {
      if (err.code !== 'API_NICHT_ERREICHBAR') setSyncInfo('Abruf-Fehler: ' + err.message);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const q = norm(search.trim());
  const visible = useMemo(() => rows.filter((tx) => matchesSearch(tx, q)), [rows, q]);
  const suggestions = useMemo(() => {
    const map = new Map();
    visible.forEach((tx) => {
      if (!['unmatched', 'partial'].includes(tx.reconciliation_status)) return;
      const best = rankCandidates(tx, openItems, { limit: 1, minScore: 90 })[0];
      if (best) map.set(tx.id, best);
    });
    return map;
  }, [visible, openItems]);

  const tabs = [
    { key: 'offen', label: 'Offen', count: counts.offen },
    { key: 'gebucht', label: 'Gebucht', count: counts.gebucht },
    { key: 'geloescht', label: 'Gelöscht', count: counts.geloescht },
    { key: 'alle', label: 'Alle' },
  ];
  const openSum = visible.filter((t) => ['unmatched', 'partial'].includes(t.reconciliation_status))
    .reduce((s, t) => s + txRest(t) * (Number(t.amount) >= 0 ? 1 : -1), 0);

  const actions = {
    onOpen: (tx, mode = 'items', category = null, preselect = null) => setSheet({ tx, mode, category, preselect }),
    onDelete: (tx) => setReasonState({ kind: 'delete', tx, run: (r) => deleteTransaction(tx.id, r, actor).then(() => toast.success('Umsatz gelöscht.')) }),
    onUndo: (tx) => setReasonState({ kind: 'undo', tx, run: (r) => unreconcile(tx.id, r, actor).then(() => toast.success('Zuordnung aufgehoben.')) }),
    onRestore: async (tx) => {
      try { await restoreTransaction(tx.id, actor); toast.success('Umsatz wiederhergestellt.'); refresh(); } catch (err) { toast.error(err.message); }
    },
  };

  return (
    <PageShell head={(
      <PageHeader icon={<Wallet className="w-5 h-5" />} title="Bank & Zahlungen"
        subtitle={syncInfo || 'Transaktionen zuordnen und verbuchen'} />
    )}>
      <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
        <section aria-label="Transaktionen" className={cn(CARD, 'min-w-0 p-3 md:p-4')}>
          <div className="flex flex-wrap gap-2 pb-3" role="group" aria-label="Konto">
            {[{ id: null, display_name: 'Alle Konten' }, ...connections].map((c) => (
              <button key={c.id ?? 'alle'} type="button" onClick={() => setConnectionId(c.id)} aria-pressed={connectionId === c.id}
                className={cn('min-h-[40px] rounded-full border px-3 text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dna-focus',
                  connectionId === c.id ? 'border-dna-primary bg-dna-primary text-white' : 'border-dna-line bg-white text-dna-primary hover:bg-dna-warm')}>
                {c.display_name}{c.linked_account_no ? <span className="ml-1 font-normal opacity-70">{c.linked_account_no}</span> : null}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-2 border-b border-dna-line pb-3 md:flex-row md:items-center md:justify-between">
            <StatusTabs tabs={tabs} value={tab} onChange={setTab} mobileKeys={['offen', 'gebucht']} ariaLabel="Status der Transaktionen" />
            <div className="relative md:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-dna-placeholder" aria-hidden="true" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, Zweck oder Betrag" aria-label="Transaktionen durchsuchen" className={cn(FIELD, 'pl-9')} />
            </div>
          </div>

          {tab === 'offen' && visible.length > 0 && (
            <p className="pt-3 text-[13px] text-dna-muted">
              <span className="font-semibold text-dna-text">{visible.length}</span> offene Zahlung(en) · Saldo <span className="tabular-nums">{fmtEUR(openSum)}</span>
              {suggestions.size > 0 && <> · <span className="font-semibold text-dna-draft-fg">{suggestions.size} mit Vorschlag</span></>}
            </p>
          )}

          {error && <p className="py-6 text-center text-sm text-red-700">{error.message}</p>}
          {isLoading && <p className="py-10 text-center text-sm text-dna-muted">Lade Transaktionen …</p>}
          {!isLoading && !error && visible.length === 0 && (
            <p className="py-12 text-center text-sm text-dna-muted">
              {tab === 'offen' ? 'Alles zugeordnet — keine offenen Zahlungen. ✓' : 'Keine Transaktionen.'}
            </p>
          )}
          {visible.length > 0 && <TransactionTable rows={visible} suggestions={suggestions} showAccount={!connectionId} actions={actions} />}
        </section>

        <aside className="space-y-4">
          <AbgleichsquoteCard />
          <BankConnectionsPanel onSynced={refresh} />
          <AbgleichKontenCard actor={actor} />
          <KontenImport connections={connections} actorEmail={actor} onChanged={refresh} />
        </aside>
      </div>

      <ReconcileSheet
        open={!!sheet}
        onOpenChange={(o) => !o && setSheet(null)}
        tx={sheet?.tx}
        initialMode={sheet?.mode}
        initialCategory={sheet?.category}
        preselect={sheet?.preselect}
        openItems={openItems}
        actor={actor}
        onDone={refresh}
      />
      <ReasonDialog state={reasonState} onClose={(changed) => { setReasonState(null); if (changed) refresh(); }} />
    </PageShell>
  );
}
